import React, { useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import ShieldIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CloseIcon from "@mui/icons-material/CloseRounded";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../services/api";
import errMsg from "../services/errMsg";
import { logoutThunk } from "../features/auth/authSlice";

const ROLE_LABELS = {
  superadmin: "Super Admin",
  admin: "Admin",
};

const initials = (s) =>
  String(s || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "U";

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        backgroundColor: "primary.light",
        color: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontSize: "0.6875rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          display: "block",
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Stack>
);

export default function ProfilePage() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const closeAndReset = () => {
    setOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!currentPassword) e.currentPassword = "Required";
    if (!newPassword) e.newPassword = "Required";
    else if (newPassword.length < 4) e.newPassword = "Min 4 characters";
    if (newPassword && currentPassword && newPassword === currentPassword) {
      e.newPassword = "Must differ from current password";
    }
    if (!confirmPassword) e.confirmPassword = "Required";
    else if (newPassword !== confirmPassword)
      e.confirmPassword = "Passwords do not match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post(
        "/auth/change-password",
        { currentPassword, newPassword },
        { skipUppercase: true }
      );
      toast.success("Password updated. Please sign in again.");
      await dispatch(logoutThunk());
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err, "Failed to update password"));
    } finally {
      setSaving(false);
    }
  };

  const displayName = user?.name || user?.username || "User";
  const username = user?.username || "";
  const role = user?.role || "";

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        mb={1.5}
      >
        <Breadcrumbs
          separator="›"
          sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "0.9375rem" }}>
            Home
          </Typography>
          <Typography
            color="text.primary"
            sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
          >
            Profile
          </Typography>
        </Breadcrumbs>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          maxWidth: 560,
          mx: { xs: 0, sm: 0 },
          overflow: "hidden",
          borderRadius: "14px",
        }}
      >
        {/* === Hero gradient === */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
            color: "common.white",
            px: { xs: 2, sm: 3 },
            pt: 3,
            pb: 3.5,
            position: "relative",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "16px",
                backgroundColor: "rgba(255,255,255,0.22)",
                border: "2px solid rgba(255,255,255,0.4)",
                color: "common.white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              {initials(displayName)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.125rem",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                }}
              >
                {displayName}
              </Typography>
              {username && (
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    opacity: 0.92,
                    fontFamily: "monospace",
                    mt: 0.25,
                  }}
                >
                  {username}
                </Typography>
              )}
              {role && (
                <Box
                  sx={{
                    display: "inline-flex",
                    mt: 0.75,
                    px: 1,
                    py: 0.25,
                    borderRadius: "999px",
                    backgroundColor: "rgba(255,255,255,0.22)",
                    border: "1px solid rgba(255,255,255,0.35)",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {ROLE_LABELS[role] || role}
                </Box>
              )}
            </Box>
          </Stack>
        </Box>

        {/* === Account info rows === */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
          <InfoRow
            icon={<BadgeIcon sx={{ fontSize: 18 }} />}
            label="Full Name"
            value={user?.name}
          />
          <Divider />
          <InfoRow
            icon={<PersonIcon sx={{ fontSize: 18 }} />}
            label="Username"
            value={username}
          />
          <Divider />
          <InfoRow
            icon={<ShieldIcon sx={{ fontSize: 18 }} />}
            label="Role"
            value={ROLE_LABELS[role] || role || "—"}
          />
        </Box>

        <Divider />

        {/* === Change Password button / collapsing form === */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Collapse in={!open}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                  Password
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Set a new sign-in password. Logs you out after the change.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<LockResetIcon fontSize="small" />}
                onClick={() => setOpen(true)}
                sx={{
                  flexShrink: 0,
                  borderRadius: "8px",
                  boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
                }}
              >
                Change Password
              </Button>
            </Stack>
          </Collapse>

          <Collapse in={open} timeout={200}>
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                mb={1.5}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      backgroundColor: "primary.light",
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LockResetIcon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                  >
                    Change Password
                  </Typography>
                </Stack>
                <IconButton
                  size="small"
                  onClick={closeAndReset}
                  disabled={saving}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>

              <form onSubmit={onSubmit} autoComplete="off">
                <Stack spacing={1.75}>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Current Password *"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    error={Boolean(errors.currentPassword)}
                    helperText={errors.currentPassword}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="New Password *"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={Boolean(errors.newPassword)}
                    helperText={
                      errors.newPassword || "Minimum 4 characters"
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Confirm New Password *"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword}
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    sx={{ pt: 0.5 }}
                  >
                    <Button
                      onClick={closeAndReset}
                      variant="outlined"
                      disabled={saving}
                      sx={{ borderRadius: "8px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                      startIcon={
                        saving ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <LockResetIcon fontSize="small" />
                        )
                      }
                      sx={{
                        borderRadius: "8px",
                        boxShadow:
                          "0 4px 10px -4px rgba(13,148,136,0.55)",
                      }}
                    >
                      {saving ? "Updating…" : "Update Password"}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </Box>
          </Collapse>
        </Box>
      </Paper>
    </Box>
  );
}
